import React, { useContext, useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { handleError } from '@domain-group/fe-helper';
import { Paginator } from '@domain-group/fe-blueprint-ui/pagination';
import { Loader, LOADER_SIZE } from '@domain-group/fe-blueprint-ui/loader';
import { StatelessTabs, Tab } from '@domain-group/fe-blueprint-ui/tabs';
import { Badge } from '@domain-group/fe-blueprint-ui-x/badge';
import Illustration from '@domain-group/fe-co-brand-illustrations';
import {
  BathIcon,
  BedIcon,
  ParkingIcon,
  StarFilledIcon,
  StarOutlineIcon,
} from '@domain-group/fe-co-icon';
import {
  ShortlistContext,
  ShortlistProvider,
} from '../../../contexts/shortlist/shortlist-context';
import {
  AgencyApiListingStatus,
  ListingFilterStatus,
  AgentProfileProperties2026QueryVariables,
  AgentProfileProperties2026Query,
  AgentProfilePropertiesSummary2026Query,
  AgentProfilePropertiesSummary2026QueryVariables,
} from '../../../generated/graphql';
import AD_COMPONENTS from '../../../constants/ad-components';
import * as styles from './index.style';
import type { ListingsContact, ProfileContact, PropertyResult } from './types';
import {
  formatPrice,
  formatPropertyType,
  getStatusBadge,
  PropertyCardListing,
} from './utils/property-formatters';

const PROPERTY_STATUSES = [
  {
    value: ListingFilterStatus.Sold,
    input: AgencyApiListingStatus.Sold,
    label: 'Sold',
    key: 'soldListings',
  },
  {
    value: ListingFilterStatus.Sale,
    input: AgencyApiListingStatus.Sale,
    label: 'For sale',
    key: 'saleListings',
  },
  {
    value: ListingFilterStatus.Leased,
    input: AgencyApiListingStatus.Leased,
    label: 'Leased',
    key: 'leasedListings',
  },
  {
    value: ListingFilterStatus.Lease,
    input: AgencyApiListingStatus.Lease,
    label: 'For rent',
    key: 'leaseListings',
  },
] as const;

type PropertyStatus = (typeof PROPERTY_STATUSES)[number]['value'];
type PropertyBucketKey = (typeof PROPERTY_STATUSES)[number]['key'];
type ShortlistContextValue = React.ContextType<typeof ShortlistContext>;

type PropertyCardProps = {
  result: PropertyResult & { listing: PropertyCardListing };
  status: PropertyStatus;
  index: number;
  isOnShortlist: boolean;
  shortlistLoading: boolean;
} & Pick<
  ShortlistContextValue,
  'addToShortlist' | 'removeFromShortlist' | 'registerScrollRef'
>;

const getShortlistButtonLabel = (
  loading: boolean,
  isOnShortlist: boolean,
): string => {
  if (loading) {
    return 'Loading shortlist';
  }

  return isOnShortlist ? 'Remove from shortlist' : 'Add to shortlist';
};

export const AGENT_PROFILE_PROPERTIES_SUMMARY_2026_QUERY = gql`
  query agentProfilePropertiesSummary2026($contactId: Int!) {
    contactByContactId(contactId: $contactId) {
      id
      firstName
      allProperties: agentsearchListingsByAgentIdV2AndAgencyId(
        page: 1
        pageSize: 1
        listingStatuses: [SOLD, SALE, LEASE, LEASED]
      ) {
        soldListings {
          total
        }
        saleListings {
          total
        }
        leaseListings {
          total
        }
        leasedListings {
          total
        }
      }
    }
  }
`;

export const AGENT_PROFILE_PROPERTIES_2026_QUERY = gql`
  query agentProfileProperties2026(
    $contactId: Int!
    $page: Int!
    $listingStatuses: [AgencyApiListingStatus!]!
  ) {
    contactByContactId(contactId: $contactId) {
      id
      filteredProperties: agentsearchListingsByAgentIdV2AndAgencyId(
        page: $page
        pageSize: 6
        listingStatuses: $listingStatuses
      ) {
        soldListings {
          ...AgentProfileProperties2026Tab
        }
        saleListings {
          ...AgentProfileProperties2026Tab
        }
        leaseListings {
          ...AgentProfileProperties2026Tab
        }
        leasedListings {
          ...AgentProfileProperties2026Tab
        }
      }
    }
  }

  fragment AgentProfileProperties2026Tab on AgentsearchListingResults {
    total
    totalPages
    results {
      saleType
      listing {
        id
        listingId
        url
        seoUrl
        listingSlug
        bedrooms
        bathrooms
        parkingSpaces
        propertyTypes
        priceDetails {
          displayPrice
        }
        soldInfo {
          soldDate
          soldPrice
          soldMethod
        }
        leasedInfo {
          leasedDate
        }
        inspectionDetails {
          inspections {
            openingDateTime
          }
        }
        media {
          __typename
          ... on Image {
            url(resolution: { width: 395, height: 225 })
          }
        }
        address {
          displayAddress
          state
        }
      }
    }
  }
`;

const getBucket = (
  data: ListingsContact['filteredProperties'],
  key: PropertyBucketKey,
) => data?.[key];

const getTotal = (
  data: ProfileContact['allProperties'],
  key: PropertyBucketKey,
): number => data?.[key]?.total ?? 0;

const PropertyCard = React.memo(
  ({
    result,
    status,
    index,
    isOnShortlist,
    shortlistLoading,
    addToShortlist,
    removeFromShortlist,
    registerScrollRef,
  }: PropertyCardProps): JSX.Element => {
    const listing = result.listing;
    const scrollToRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      registerScrollRef?.(
        `${listing.listingId}-${AD_COMPONENTS.AgentProfileProperties}`,
        scrollToRef,
      );
    }, [listing.listingId, registerScrollRef]);
    const image = listing.media?.find(
      (mediaItem) => 'url' in mediaItem && typeof mediaItem.url === 'string',
    );
    const imageUrl = image && 'url' in image ? image.url : undefined;
    const address = listing.address?.displayAddress ?? '';
    const [street, ...locality] = address.split(',');
    const listingUrl = listing.listingSlug
      ? `/${listing.listingSlug}`
      : listing.url || listing.seoUrl || '#';
    const badge = getStatusBadge(listing, status);
    const price = formatPrice(listing, status);

    return (
      <article
        ref={scrollToRef}
        css={styles.propertyCard}
        style={{ '--card-index': index } as React.CSSProperties}
        data-testid="agent-profile-properties-2026__card"
      >
        <a
          href={listingUrl}
          css={styles.cardLink}
          aria-label={`View property at ${address}`}
        >
          <div css={styles.imageWrap}>
            {imageUrl ? (
              <img src={imageUrl} alt="" css={styles.image} />
            ) : (
              <div css={styles.imageFallback} />
            )}
            {badge && (
              <div css={styles.statusBadge(badge.family)}>
                <Badge family={badge.family}>{badge.label}</Badge>
              </div>
            )}
          </div>
        </a>
        <div css={styles.cardContent}>
          <div css={styles.priceRow}>
            <a href={listingUrl} css={styles.cardLink}>
              <p css={styles.price}>{price}</p>
            </a>
            <button
              type="button"
              css={styles.shortlistButton}
              aria-label={getShortlistButtonLabel(
                shortlistLoading,
                isOnShortlist,
              )}
              aria-busy={shortlistLoading || undefined}
              aria-pressed={isOnShortlist}
              disabled={shortlistLoading}
              onClick={() =>
                isOnShortlist
                  ? removeFromShortlist?.(listing.listingId)
                  : addToShortlist?.(listing.listingId)
              }
            >
              {isOnShortlist ? (
                <StarFilledIcon size="custom" aria-hidden />
              ) : (
                <StarOutlineIcon size="custom" aria-hidden />
              )}
            </button>
          </div>
          <a href={listingUrl} css={styles.detailsLink}>
            <p css={styles.address}>
              <span css={styles.addressLine}>
                {locality.length ? `${street},` : street}
              </span>
              {locality.length > 0 && (
                <span css={styles.addressLine}>
                  {locality.join(',').trim()}
                </span>
              )}
            </p>
            <div css={styles.propertyDetails}>
              {listing.bedrooms != null && (
                <span css={styles.feature}>
                  <BedIcon size="small" />
                  {listing.bedrooms}
                </span>
              )}
              {listing.bathrooms != null && (
                <span css={styles.feature}>
                  <BathIcon size="small" />
                  {listing.bathrooms}
                </span>
              )}
              {listing.parkingSpaces != null && (
                <span css={styles.feature}>
                  <ParkingIcon size="small" />
                  {listing.parkingSpaces}
                </span>
              )}
              <span css={styles.propertyType}>
                {formatPropertyType(listing.propertyTypes?.[0])}
              </span>
            </div>
          </a>
        </div>
      </article>
    );
  },
);

const PropertiesSection = ({
  contactId,
}: {
  contactId: number;
}): JSX.Element | null => {
  const shortlist = useContext(ShortlistContext);
  const [selectedStatus, setSelectedStatus] = useState<PropertyStatus>();
  const [page, setPage] = useState(1);
  const listingsRef = useRef<HTMLDivElement>(null);
  const summary = useQuery<
    AgentProfilePropertiesSummary2026Query,
    AgentProfilePropertiesSummary2026QueryVariables
  >(AGENT_PROFILE_PROPERTIES_SUMMARY_2026_QUERY, {
    variables: { contactId },
  });
  const contact = summary.data?.contactByContactId;
  const allProperties = contact?.allProperties;
  const totalProperties = PROPERTY_STATUSES.reduce(
    (total, item) => total + getTotal(allProperties, item.key),
    0,
  );
  const defaultStatus =
    PROPERTY_STATUSES.find((item) => getTotal(allProperties, item.key) > 0)
      ?.value ?? ListingFilterStatus.Sold;
  const activeStatus = selectedStatus ?? defaultStatus;
  const activeDetails = PROPERTY_STATUSES.find(
    (item) => item.value === activeStatus,
  )!;
  const listings = useQuery<
    AgentProfileProperties2026Query,
    AgentProfileProperties2026QueryVariables
  >(AGENT_PROFILE_PROPERTIES_2026_QUERY, {
    variables: {
      contactId,
      page,
      listingStatuses: [activeDetails.input],
    },
    skip: !allProperties || totalProperties === 0,
    fetchPolicy: 'cache-first',
  });
  const error = summary.error ?? listings.error;
  const loading = summary.loading || listings.loading;
  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);
  const activeBucket = getBucket(
    listings.data?.contactByContactId?.filteredProperties,
    activeDetails.key,
  );
  const results = (activeBucket?.results ?? []).filter(
    (result): result is PropertyResult & { listing: PropertyCardListing } =>
      !!result?.listing,
  );
  const totalPages = activeBucket?.totalPages ?? 0;
  const hasNoResults =
    !loading &&
    (!activeBucket || activeBucket.total === 0 || results.length === 0);

  if (!loading && (error || (allProperties && totalProperties === 0))) {
    return null;
  }

  const resetPage = () => {
    setPage(1);
    listingsRef.current?.scrollTo?.({ left: 0, behavior: 'smooth' });
  };
  const changePage = (event: React.FormEvent<EventTarget>, newPage: number) => {
    event.preventDefault();
    setPage(newPage);
    listingsRef.current?.scrollTo?.({ left: 0, behavior: 'smooth' });
  };

  return (
    <section css={styles.section} data-testid="agent-profile-properties-2026">
      <h2 css={styles.heading}>
        {contact?.firstName ? `${contact.firstName}'s` : "Agent's"} properties
      </h2>
      <div css={styles.tabsViewport}>
        <StatelessTabs
          activeTab={activeStatus}
          onTabClick={(nextTab) => {
            if (!nextTab) {
              return;
            }
            setSelectedStatus(nextTab as PropertyStatus);
            resetPage();
          }}
        >
          {PROPERTY_STATUSES.map((item) => {
            const count = getTotal(allProperties, item.key);
            return (
              <Tab
                key={item.value}
                label={
                  count === 0 ? (
                    <>
                      {item.label}
                      <span css={styles.zeroTabCount}>(0)</span>
                    </>
                  ) : (
                    item.label
                  )
                }
                itemCount={count || undefined}
              />
            );
          })}
        </StatelessTabs>
      </div>
      {loading && (
        <div css={styles.loading} role="status" aria-label="Loading properties">
          <Loader size={LOADER_SIZE.LARGE} />
        </div>
      )}
      {!loading && hasNoResults && (
        <div
          css={styles.noResults}
          data-testid="agent-profile-properties-2026__no-results"
        >
          <Illustration
            type="spot"
            name="niceSuburb"
            size="regular"
            shape="circle"
          />
          <p css={styles.noResultsHeading}>No exact matches</p>
          <p css={styles.noResultsCopy}>
            Change your filters to see more of{' '}
            {contact?.firstName ?? 'this agent'}&apos;s properties
          </p>
        </div>
      )}
      {!loading && !hasNoResults && (
        <>
          <div
            ref={listingsRef}
            css={styles.listings}
            data-testid="agent-profile-properties-2026__listings"
          >
            {results.map((result, index) => (
              <PropertyCard
                key={result.listing.listingId}
                result={result}
                status={activeStatus}
                index={index}
                isOnShortlist={shortlist.adIdList.includes(
                  result.listing.listingId,
                )}
                shortlistLoading={shortlist.loading}
                addToShortlist={shortlist.addToShortlist}
                removeFromShortlist={shortlist.removeFromShortlist}
                registerScrollRef={shortlist.registerScrollRef}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div
              css={styles.pagination}
              data-testid="agent-profile-properties-2026__pagination"
            >
              <Paginator
                small
                totalPages={totalPages}
                currentPage={page}
                urlTemplate="?page={{ page }}"
                onPageChange={changePage}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

const AgentProfileProperties2026 = ({
  contactId,
}: {
  contactId: number;
}): JSX.Element => (
  <ShortlistProvider>
    <PropertiesSection contactId={contactId} />
  </ShortlistProvider>
);

export default AgentProfileProperties2026;
